// components/DashboardLayout.js
import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  Receipt,
  ReceiptLong,
  AccountCircle,
  Logout,
  // LocalOffer, // Hidden - Order Bidding managed by Handoff admin only
  Hub,
  Settings,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../services/auth-service';
import { ReactComponent as HandoffLogo } from '../assets/handoff-logo.svg';
import { track, routePattern } from '../utils/analytics';

const drawerWidth = 260;

const DashboardLayout = ({ children, retailer }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await authService.logout();
    handleProfileMenuClose();
  };

  const isOffline = retailer?.status === 'inactive';
  const offlineSince = retailer?.status_changed_at
    ? new Date(retailer.status_changed_at).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null;

  const menuItems = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/' },
    { text: 'Commissions', icon: <Receipt />, path: '/commissions' },
    { text: 'Invoices', icon: <ReceiptLong />, path: '/invoices' },
    { text: 'Network', icon: <Hub />, path: '/network' },
    // Settings is the last entry (billing spec §4.1); its tabs live under /settings/*.
    { text: 'Settings', icon: <Settings />, path: '/settings' },
    // Order Bidding hidden - bids now managed by Handoff admin only (8+2 revenue model)
    // { text: 'Order Bidding', icon: <LocalOffer />, path: '/order-bidding' },
  ];

  const drawer = (
    <Box>
      <Toolbar>
        <HandoffLogo style={{ height: 24, width: 'auto' }} />
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path)}
              onClick={() => {
                track('Retailer Menu Clicked', { item: item.text, from_path: routePattern(location.pathname) });
                navigate(item.path);
                if (isMobile) setMobileOpen(false);
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {retailer?.name || 'Retailer Dashboard'}
          </Typography>
          <IconButton
            size="large"
            edge="end"
            aria-label="account menu"
            color="inherit"
            onClick={handleProfileMenuOpen}
          >
            <Avatar sx={{ width: 32, height: 32 }}>
              {retailer?.name?.charAt(0) || 'R'}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem
              onClick={() => {
                handleProfileMenuClose();
                navigate('/settings');
              }}
            >
              <ListItemIcon>
                <AccountCircle fontSize="small" />
              </ListItemIcon>
              Profile
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minWidth: 0,
          overflowX: 'auto',
        }}
      >
        <Toolbar />
        {isOffline && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Handoff is currently offline for your store
            {offlineSince ? ` (since ${offlineSince})` : ''}. While you're
            inactive, you won't receive or send shoppers, so your data may look
            incomplete. Contact us to come back online.
          </Alert>
        )}
        {children}
      </Box>
    </Box>
  );
};

export default DashboardLayout;